/**
 * Single source of truth for converting income frequencies into a monthly amount.
 * Used across stores, services, and views — keep here so the conversion factors
 * (e.g. 4.33 weeks/month) are defined once.
 */

export type IncomeFrequency = "weekly" | "bi-weekly" | "monthly" | "quarterly" | "annually" | "one-time"

interface IncomeLike {
  amount: number
  frequency?: string | null
}

const WEEKS_PER_MONTH = 4.33
const BIWEEKS_PER_MONTH = 2.17

export function toMonthly(amount: number, frequency: string | null | undefined): number {
  if (!Number.isFinite(amount)) return 0
  switch (frequency) {
    case "weekly":
      return amount * WEEKS_PER_MONTH
    case "bi-weekly":
      return amount * BIWEEKS_PER_MONTH
    case "quarterly":
      return amount / 3
    case "annually":
      return amount / 12
    case "one-time":
      return 0
    case "monthly":
    case null:
    case undefined:
    default:
      return amount
  }
}

export function calculateMonthlyIncome(incomes: ReadonlyArray<IncomeLike>): number {
  return incomes.reduce((total, income) => total + toMonthly(income.amount, income.frequency), 0)
}

interface MemberIncomeLike extends IncomeLike {
  userId: string
  isActive?: boolean
}

/**
 * Proportion of household income earned by each member, as integer percentages
 * that sum to exactly 100. Used to seed an income-based expense split so the
 * higher earner covers a proportionally larger share.
 *
 * Income is summed per member (monthly-equivalent, ignoring paused sources and
 * income owned by non-members). When nobody has any tracked income the split
 * falls back to an even share so the result is always usable.
 */
export function incomeSplitPercentages(
  incomes: ReadonlyArray<MemberIncomeLike>,
  memberIds: ReadonlyArray<string>,
): Record<string, number> {
  if (memberIds.length === 0) return {}

  const monthly = new Map<string, number>()
  for (const id of memberIds) monthly.set(id, 0)
  for (const income of incomes) {
    if (income.isActive === false) continue
    if (!monthly.has(income.userId)) continue
    monthly.set(income.userId, (monthly.get(income.userId) ?? 0) + toMonthly(income.amount, income.frequency))
  }

  const total = memberIds.reduce((sum, id) => sum + (monthly.get(id) ?? 0), 0)
  // No income data → even split (each member's exact equal share, summed to 100).
  const rawShares = memberIds.map((id) => (total > 0 ? ((monthly.get(id) ?? 0) / total) * 100 : 100 / memberIds.length))

  return largestRemainderRound(memberIds, rawShares)
}

/**
 * Round a set of fractional percentages to integers that still sum to 100,
 * handing each leftover point to the members with the largest remainders.
 */
function largestRemainderRound(ids: ReadonlyArray<string>, shares: ReadonlyArray<number>): Record<string, number> {
  const floored = shares.map((s) => Math.floor(s))
  let remaining = 100 - floored.reduce((sum, n) => sum + n, 0)
  const order = ids.map((_, i) => ({ i, remainder: shares[i] - floored[i] })).sort((a, b) => b.remainder - a.remainder)

  const result: Record<string, number> = {}
  ids.forEach((id, i) => {
    result[id] = floored[i]
  })
  for (let k = 0; k < order.length && remaining > 0; k++) {
    result[ids[order[k].i]] += 1
    remaining--
  }
  return result
}
